<script setup lang="ts">
import { computed } from "vue";
import type { QuoteDocument } from "@kekkle/shared/types/documents/quote-document/quote-document";
import { timeAgo } from "@/helpers/dates/time-ago";
import { formatMemberNamesMap } from "@/helpers/utils/format-member-names";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useI18n } from "vue-i18n";

type QuoteWithMetadata = QuoteDocument & {
  reported_by?: string[] | null;
  number_of_comments?: number;
};

type Props = {
  quoteData: QuoteWithMetadata;
  quoteId: string;
};

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: "click", quoteData: QuoteWithMetadata): void;
}>();

const { t } = useI18n();
const groupsStore = useGroupsStore();

const quote = computed(() => props.quoteData.quote ?? "");

const authorNames = computed(() => {
  if (!groupsStore.groupData?.members) return {};
  return formatMemberNamesMap(
    props.quoteData.authors,
    groupsStore.groupData.members,
    t("global.deleted_user"),
  );
});

const authorLine = computed(() => {
  const names = Object.values(authorNames.value);
  const custom = props.quoteData.custom_author;
  if (custom) names.push(custom);
  return names.join(", ");
});

const formattedTimeAgo = computed(() => {
  return props.quoteData.timestamp ? timeAgo(props.quoteData.timestamp) : "";
});

const isReported = computed(() => {
  return props.quoteData.reported_by && props.quoteData.reported_by.length > 0;
});
</script>

<template>
  <div
    v-if="!isReported"
    class="flex cursor-pointer flex-col justify-between rounded-lg border-2 border-black-dark bg-white p-3 shadow-red-hard-sm transition-all hover:shadow-red-hard active:translate-y-0.5 active:shadow-none dark:bg-kekkle-dark-light"
    @click="emit('click', quoteData)"
  >
    <div>
      <p class="text-xs italic text-gray-400 dark:text-slate-500">
        {{ formattedTimeAgo }}
      </p>
      <p
        translate="no"
        class="mt-1 line-clamp-4 break-words text-sm font-semibold dark:text-slate-200"
      >
        {{ quote }}
      </p>
    </div>
    <p
      v-if="authorLine"
      class="mt-2 truncate text-right text-xs text-gray-600 dark:text-slate-400"
    >
      — {{ authorLine }}
    </p>
  </div>
</template>
